import {addUrlToHistoryStack} from '@github-ui/history'
import type {ItemIdentifier} from '@github-ui/issue-viewer/Types'
import {useParams} from '@github-ui/react-router'
import {ssrSafeLocation} from '@github-ui/ssr-utils'
import {startTransition, useCallback, useEffect, useMemo, useState} from 'react'

import {SEARCH_PARAMS} from '../constants/search-params'

type UrlParams = {
  owner: string
  repo: string
  number: string
  viewId: string
  type: string
}

function parseSidePanelIdentifierFromUrl(): ItemIdentifier | null {
  const url = new URL(ssrSafeLocation.href, ssrSafeLocation.origin)
  const nwoReference = url.searchParams.get(SEARCH_PARAMS.issue)

  if (!nwoReference) return null

  const [owner, repo, issueNumber] = nwoReference.split('|')
  if (!(owner && repo && issueNumber)) return null

  const number = isNaN(parseInt(issueNumber)) ? undefined : parseInt(issueNumber)
  if (!number) return null

  return {
    number,
    owner,
    repo,
    type: 'Issue',
  } as const
}

export const useRouteInfo = () => {
  const {owner, repo, number, viewId} = useParams<UrlParams>()
  const [internalSidePanelIdentifier, setInternalSidePanelIdentifier] = useState<ItemIdentifier | null>(null)

  const setSidePanelItemIdentifier = useCallback((identifier: ItemIdentifier | null) => {
    const url = new URL(ssrSafeLocation.href, ssrSafeLocation.origin)
    if (identifier) {
      url.searchParams.set(SEARCH_PARAMS.issue, `${identifier.owner}|${identifier.repo}|${identifier.number}`)
    } else {
      url.searchParams.delete(SEARCH_PARAMS.issue)
    }
    startTransition(() => {
      setInternalSidePanelIdentifier(identifier)
    })
    addUrlToHistoryStack(url.toString())
  }, [])

  // Read the URL post-mount to set initial side panel state.
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setInternalSidePanelIdentifier(parseSidePanelIdentifierFromUrl())
  }, [])

  const sidePanelItemURL = internalSidePanelIdentifier
    ? `/${internalSidePanelIdentifier.owner}/${internalSidePanelIdentifier.repo}/issues/${internalSidePanelIdentifier.number}`
    : ''

  const itemIdentifier = useMemo(() => {
    let parsedNumber = number ? parseInt(number, 10) : undefined
    if (parsedNumber && parsedNumber < 1) {
      parsedNumber = undefined
    }
    return owner && repo && parsedNumber ? ({owner, repo, number: parsedNumber} as ItemIdentifier) : undefined
  }, [number, owner, repo])

  const onCloseSidePanel = useCallback(() => {
    setSidePanelItemIdentifier(null)
  }, [setSidePanelItemIdentifier])

  const onParentIssueActivate = useCallback(
    (e: React.MouseEvent | React.KeyboardEvent, parentIdentifier: ItemIdentifier): boolean => {
      if (!itemIdentifier) return false

      // Only close the side panel if the parent is the same as the current issue
      if (
        parentIdentifier.owner !== itemIdentifier.owner ||
        parentIdentifier.repo !== itemIdentifier.repo ||
        parentIdentifier.number !== itemIdentifier.number
      ) {
        return false
      }

      e.preventDefault()
      onCloseSidePanel()
      return true
    },
    [itemIdentifier, onCloseSidePanel],
  )

  useEffect(() => {
    function onPopState() {
      const identifier = parseSidePanelIdentifierFromUrl()

      if (!identifier) {
        setInternalSidePanelIdentifier(null)
        return
      }

      // Don't open the side panel for the issue that is currently being viewed
      if (
        identifier.owner === itemIdentifier?.owner &&
        identifier.repo === itemIdentifier?.repo &&
        identifier.number === itemIdentifier?.number
      ) {
        return
      }

      setInternalSidePanelIdentifier(identifier)
    }

    window.addEventListener('popstate', onPopState)

    return () => window.removeEventListener('popstate', onPopState)
  }, [itemIdentifier])

  return {
    itemIdentifier,
    viewId,
    sidePanelItemIdentifier: internalSidePanelIdentifier,
    setSidePanelItemIdentifier,
    sidePanelItemURL,
    onCloseSidePanel,
    onParentIssueActivate,
  }
}
